import type { NewPaymentMethodFn } from '@/features/billing'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { BillingMethodListItemViewModel } from '@/features/billing/payment-methods/BillingMethodListItemViewModel'
import { DeleteBillingMethodDialogViewModel } from '@/features/billing/payment-methods/DeleteBillingMethodDialog/DeleteBillingMethodDialogViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { computed } from 'mobx'
import { task } from 'mobx-task'

/**
 * View model for a member's own payment methods.
 */
export class ProfilePaymentMethodsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Confirms deleting a payment method.
   */
  readonly deleteDialogVm

  /**
   * The payment methods.
   */
  readonly methods

  constructor(
    public readonly member: Member,
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _newPaymentMethod: NewPaymentMethodFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.deleteDialogVm = new DeleteBillingMethodDialogViewModel(
      this._billingProfileStore,
      this._createAlertErrorHandler,
    )
    this.methods = new ViewModelList({
      models: () => this.paymentMethods,
      create: (method) =>
        new BillingMethodListItemViewModel(
          method,
          this._billingProfileStore,
          this.deleteDialogVm,
          this._createAlertErrorHandler,
        ),
    })
    this._lifecycle.manage(this.methods)
  }

  /**
   * The billing profile, once loaded.
   */
  @computed
  get billingProfile() {
    return this._billingProfileStore.individualBillingProfile(this.member)
  }

  /**
   * The payment methods of the billing profile.
   */
  @computed
  get paymentMethods() {
    return this.billingProfile?.billingMethods.slice() ?? []
  }

  /**
   * Whether the current user may delete the payment methods.
   */
  @computed
  get canDeletePaymentMethods() {
    return this.billingProfile
      ? this._billingProfileStore.canManage(this.billingProfile)
      : false
  }

  /**
   * Whether the list is busy with an action.
   */
  @computed
  get disabled() {
    return (
      this.addPaymentMethod.pending ||
      this.methods.items.some((methodVm) => methodVm.setAsDefault.pending)
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this.deleteDialogVm.modal.close()
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the member's billing profile.
   */
  fetch = task(async (signal: AbortSignal) => {
    const withRetry = this._createAlertRetryWrapper(
      'Loading payment methods failed',
      signal,
    )
    await withRetry(() =>
      this._billingProfileStore.fetchBillingProfileForIndividual(
        this.member.workspace.id,
        this.member.user.publicId,
      ),
    )
  })

  /**
   * Add a payment method.
   */
  addPaymentMethod = task.resolved(async () => {
    const method = await this._newPaymentMethod(
      this.member,
      null,
      this._lifecycle.signal,
    )

    if (!method) {
      return
    }

    await this.methods.get(method)?.setAsDefault()
  })
}
